import React, { ChangeEvent, useContext, useEffect, useMemo } from 'react';
import CancelIcon from 'components/svg/cancel.svg';
import { ListItemText, SelectProps, SvgIcon } from '@mui/material';
import { useTranslation } from 'i18n';
import { ParsedUrlQueryInput } from 'querystring';
import { useQueryParams } from 'hooks';

import Button from 'components/common/Button';
import MenuItem from 'components/common/MenuItem';
import Checkbox from 'components/common/Checkbox';
import ListItemIcon from 'components/common/ListItemIcon';
import { filterValue } from './utils';
import FilterSelect from './FilterSelect';
import { FilterNameContext } from './FilterNameProvider';

export interface IFilterProps<T> {
  name: string;
  // eslint-disable-next-line react/no-unused-prop-types
  placeholder?: string;
  defaultValue?: any;
  multiple?: boolean;
  disabled?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  displayEmpty?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  value?: string | null;
  items: T[];
  allItems?: T[];
  labelFormatter: (item: T) => React.ReactNode;
  valueFormatter: (item: T) => string;
  valueTransformer: (
    event: ChangeEvent<HTMLInputElement>,
    queryParams: Record<string, string | string[]>,
  ) => ParsedUrlQueryInput;
  clearValueParser: (queryParams: Record<string, string | string[]>, name?: string) => ParsedUrlQueryInput;
  valueParser: (queryParams: Record<string, string | string[]>, name: string) => string | string[];
  isClearButton?: boolean;
}

const getItemFromValue = (values: unknown, items: any) => {
  const value = Array.isArray(values) ? values[0] : values;
  return items.find((item: any) => Number(value) === item.id || value === item.slug || value === item);
};

const createSelectProps = (
  { multiple, placeholder, labelFormatter, items }: IFilterProps<any>,
  availableItems: Set<string>,
): SelectProps => ({
  displayEmpty: true,
  multiple,
  MenuProps: {
    variant: 'menu',
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: -8,
      horizontal: 'left',
    },
  },
  renderValue: (value) => {
    const availableValue = filterValue(value as string | string[], availableItems);
    if ((availableValue as string[]).length === 0 || !availableValue) return placeholder;
    if (!Array.isArray(availableValue) && typeof items[0] === 'string') return labelFormatter(availableValue);
    if ((availableValue as string[]).length === 1 || typeof availableValue === 'string') {
      const item = getItemFromValue(availableValue, items);
      return labelFormatter(item);
    }
    const item = getItemFromValue(availableValue, items);
    return `${labelFormatter(item)} +${(availableValue as string[]).length - 1}`;
  },
});

const FilterSelectHandler = <T,>(props: IFilterProps<T>) => {
  const {
    name,
    disabled,
    items,
    labelFormatter,
    valueFormatter,
    valueTransformer,
    clearValueParser,
    valueParser,
    defaultValue,
    multiple,
    allItems = items,
    isClearButton = true,
  } = props;
  const [t] = useTranslation();
  const { setFiltersName } = useContext(FilterNameContext);

  const [queryParams, setQueryParams] = useQueryParams();
  const availableValues = useMemo(() => new Set(allItems.map(valueFormatter)), [allItems, valueFormatter]);
  const availableItems = useMemo(() => new Set(items.map(valueFormatter)), [items, valueFormatter]);

  const valueQueryParams = useMemo(
    () => ({
      ...queryParams,
      [name]: queryParams[name] || defaultValue,
    }),
    [defaultValue, name, queryParams],
  );
  const selectedValue = filterValue(valueParser(valueQueryParams, name), availableValues);

  const handleFilterSelectChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const value = valueTransformer(event, queryParams);
    setQueryParams({
      ...queryParams,
      ...value,
    });
  };

  const handleClearQuery: React.MouseEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const value = clearValueParser(queryParams, name);
    setQueryParams({
      ...queryParams,
      ...value,
    });
  };

  useEffect(() => {
    setFiltersName((state: any) => {
      const queryValue = valueTransformer(
        {
          target: {
            value: selectedValue,
            name,
          },
        } as ChangeEvent<HTMLInputElement>,
        valueQueryParams,
      );
      if (queryValue && Object.keys(queryValue).every((key) => Array.isArray(state[name]) && state[name].includes(key)))
        return state;
      return {
        ...state,
        [name]: queryValue ? Object.keys(queryValue).filter((key) => key) : [],
      };
    });
  }, [name, selectedValue, setFiltersName, valueQueryParams, valueTransformer]);

  return (
    <FilterSelect
      disabled={disabled}
      name={name}
      value={selectedValue}
      availableItems={availableItems}
      SelectProps={createSelectProps(props, availableItems)}
      onChange={handleFilterSelectChange}
    >
      {items.map((item) => {
        const value = valueFormatter(item);
        const label = labelFormatter(item);
        return (
          <MenuItem
            small
            key={value}
            disabled={disabled}
            value={value}
            onClick={value === selectedValue && !defaultValue && !multiple && handleClearQuery}
            multiple={multiple}
          >
            {multiple && (
              <ListItemIcon spacing={1}>
                <Checkbox
                  dense
                  disableRipple
                  color="primary"
                  checked={typeof selectedValue === 'string' ? selectedValue === value : selectedValue.includes(value)}
                />
              </ListItemIcon>
            )}
            <ListItemText primary={label} />
          </MenuItem>
        );
      })}
      {isClearButton && !defaultValue && (
        // TODO: button={false}
        <MenuItem small justifyContent="end">
          <Button
            size="small"
            color="secondary"
            startIcon={<SvgIcon color="secondary" component={CancelIcon} viewBox="0 0 16 16" />}
            onClick={handleClearQuery}
          >
            {t<string>('common:button.clear')}
          </Button>
        </MenuItem>
      )}
    </FilterSelect>
  );
};

export default FilterSelectHandler;
